import {
  Component,
  Output,
  EventEmitter,
  inject,
  ChangeDetectionStrategy,
  signal,
  ViewChild,
  ElementRef,
} from '@angular/core';

import { FileUploadService } from '../../../../../core/services/file-upload.service';

const MAX_FILE_SIZE_BYTES = 26 * 1024 * 1024; // 26 MB
const MAX_FILE_SIZE_LABEL = '26MB';
const MAX_FILES = 50;
const ALLOWED_EXTENSION = '.txt';

@Component({
  selector: 'app-file-drop-zone',
  standalone: true,
  imports: [],
  templateUrl: './file-drop-zone.component.html',
  styleUrls: ['./file-drop-zone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileDropZoneComponent {
  private readonly fileUploadService = inject(FileUploadService);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Output() filesUploaded = new EventEmitter<void>();

  readonly isDragOver = signal(false);
  readonly fileErrors = signal<string[]>([]);

  readonly maxFileSizeLabel = MAX_FILE_SIZE_LABEL;
  readonly maxFiles = MAX_FILES;
  readonly allowedExtension = ALLOWED_EXTENSION;

  /** Guard to prevent double-open of native file picker */
  private isFilePickerOpen = false;

  openFilePicker(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.isFilePickerOpen) return;

    this.isFilePickerOpen = true;
    this.fileInput?.nativeElement?.click();
    setTimeout(() => (this.isFilePickerOpen = false), 500);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.processFiles(Array.from(input.files));
    }
    // Reset so the same file can be re-selected after removal
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    if (event.dataTransfer?.files.length) {
      this.processFiles(Array.from(event.dataTransfer.files));
    }
  }

  clearErrors(): void {
    this.fileErrors.set([]);
  }

  private processFiles(files: File[]): void {
    const errors: string[] = [];
    const validFiles: File[] = [];

    for (const file of files) {
      // 1. Extension check — only .txt
      if (!file.name.toLowerCase().endsWith(ALLOWED_EXTENSION)) {
        errors.push(
          `"${file.name}" is not allowed. Only ${ALLOWED_EXTENSION} files are accepted.`
        );
        continue;
      }

      // 2. File size check — max 26MB
      if (file.size > MAX_FILE_SIZE_BYTES) {
        errors.push(
          `"${file.name}" exceeds the maximum file size of ${MAX_FILE_SIZE_LABEL}.`
        );
        continue;
      }

      // 3. Duplicate filename check
      if (this.fileUploadService.isDuplicate(file.name)) {
        errors.push(`"${file.name}" has already been added.`);
        continue;
      }

      validFiles.push(file);
    }

    // 4. Max files check — enforce total limit across current + incoming
    const currentCount = this.fileUploadService.currentFileCount;
    const remaining = MAX_FILES - currentCount;

    if (validFiles.length > remaining) {
      const dropped = validFiles.splice(remaining);
      dropped.forEach(f =>
        errors.push(
          `"${f.name}" was not added. Maximum of ${MAX_FILES} files per request reached.`
        )
      );
    }

    this.fileErrors.set(errors);

    validFiles.forEach(file => {
      this.fileUploadService.addFile(file);
      this.filesUploaded.emit();
    });
  }
}
